import { useCallback, useState, useRef } from 'react';
import type { ReactElement } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { ButtonWithDropdown } from '@simplywallst/ui-core';
import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import MarketInsightsOutline from '@simplywallst/ui-core/icons/MarketInsightsOutline';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

const UpdatePortfolioCurrencyModal = loadable(
  () => import('@/components/UpdatePortfolioCurrencyModal')
);

type Option = 'change-currency';

interface Props {
  portfolio: Portfolio;
}

export const PortfolioMoreOptions = ({ portfolio }: Props) => {
  const { trackEvent } = useTracking();

  const [modal, setModal] = useState<Option | null>(null);
  const handleCloseModal = useCallback(() => setModal(null), []);
  const handleSelect = useCallback((value: string) => {
    setModal(value as Option);
  }, []);

  const { current: options } = useRef<
    { label: string; value: Option; icon: ReactElement }[]
  >([
    {
      label: 'Change currency',
      value: 'change-currency' as const,
      icon: <MarketInsightsOutline />,
    },
  ]);

  return (
    <>
      <ButtonWithDropdown
        styleType="passive"
        data-cy-id="dashboard-portfolio-more-options"
        options={options}
        onSelect={handleSelect}
        onClick={() => {
          trackEvent({
            action: 'click',
            modifier: 'portfolioMoreOptions',
          });
        }}
      >
        <MoreHorizontalSolid aria-label="Portfolio menu" />
      </ButtonWithDropdown>

      {modal === 'change-currency' ? (
        <UpdatePortfolioCurrencyModal
          portfolio={portfolio}
          onClose={handleCloseModal}
        />
      ) : null}
    </>
  );
};
